import React, {useEffect, useState} from 'react';
import {checkNullArr, checkNullJson, checkNullStr, getArrIndexValue, getJsonValue, isBoolTrue, printError, printLog} from "../utils/ReactUtils";
import {Button, Flexbox, Label, Section} from "../index";

/**
 * Likert Component Props.
 *
 * @example
 * Example Implementation:
 * <LikertView
 *   borderBoxClassName="likert-border-box"
 *   stmtItemClassName="likert-statement-item"
 *   optionItemClassName="likert-statement-item"
 *   style={{ background: '#f0f0f0' }}
 *   data = {
 *         statements: [
 *             {
 *                 id: 1,
 *                 statement: 'Agree',
 *             },
 *             {
 *                 id: 2,
 *                 statement: 'Satisfaction',
 *             },
 *         ],
 *         optionColumns: ['Agree', 'Not agree', 'Strongly disagree',]
 *     };
 *   showQuestion={true}
 *   showLoader={false}
 * />
 *
 * @param {object} LikertProps
 * @param {string} [className=''] - Additional CSS class for the main container.
 * @param {string} [borderBoxClassName=''] - Additional CSS class for the border container.
 * @param {string} [statementItemClassName=''] - Additional CSS class for each Likert statement item.
 * @param {string} [optionBoxClassName=''] - Additional CSS class for the options container.
 * @param {string} [optionItemClassName=''] - Additional CSS class for each Likert option item.
 * @param {object} [style={}] - Inline styles for the main container.
 * @param {object} [data={statements: [], optionColumns: []}] - Default statements for the Likert scale.
 * @param {boolean} [showQuestion=true] - Flag to control the visibility of the Likert question.
 * @param {boolean} [showLoader=false] - Flag to show a loader while processing data.
 * @param {boolean} [roundedVariant=3] - Corner-radius value.
 */
const LikertView = React.memo(({
                                   borderBoxClassName = '',
                                   tableClassName = '',
                                   addStmtClassName = '',
                                   editStmtClassName = '',
                                   deleteStmtClassName = '',
                                   addOptionClassName = '',
                                   editOptionClassName = '',
                                   deleteOptionClassName = '',
                                   stmtItemClassName = '',
                                   optionItemClassName = '',
                                   disableSelection = false,
                                   data = {statements: [], optionColumns: []},
                                   showLoader = false,
                                   previewMode = false,

                                   stmtIdKey = 'id',
                                   stmtTextKey = 'statement',
                                   stmtsKey = 'statements',
                                   optionsColKey = 'optionColumns',
                                   optionsAnswersKey = 'answers',

                                   roundedVariant = 4,
                                   onUpdate,
                                   onPreviewUpdate,

                                   borderColor = 'gray',
                                   color = 'black',

                                   borderBoxStyle = {},
                                   addOptionStyle = {},
                                   editOptionStyle = {},
                                   deleteOptionStyle = {},
                                   addStmtStyle = {},
                                   tableStyle = {},
                                   editStmtStyle = {},
                                   deleteStmtStyle = {},
                                   optionItemStyle = {},
                                   stmtItemStyle = {},
                               }) => {
    const TAG = 'LikertView';

    const CONTROL_SIZE = '24px';
    const CONTROL_RIPPLE = 'var(--ripple-color-light)';

    // const stmtIdKey = 'id';
    // const stmtTextKey = 'statement';
    // const stmtsKey = 'statements';
    // const optionsColKey = 'optionColumns';


    const likertData = {
        statements: [
            {
                id: 1,
                statement: 'Agree',
            },
            {
                id: 2,
                statement: 'Satisfaction',
            },
        ],
        optionColumns: ['Agree', 'Not agree', 'Strongly disagree',]
    };

    // const [statements, setStatements] = useState(likertData[stmtsKey]);
    // const [optionColumns, setOptionColumns] = useState(likertData[optionsColKey]);
    const [statements, setStatements] = useState([]);
    const [optionColumns, setOptionColumns] = useState([]);
    const [optionAnswers, setOptionAnswers] = useState({});
    const [selectedOptions, setSelectedOptions] = useState({});//{stmt1: option1}
    const [selectedOptionsIndices, setSelectedOptionsIndices] = useState({});//{0: option1}

    useEffect(() => {
        // log('useEffect.data:', data);
        try {
            const stmts = getJsonValue(data, stmtsKey, []);
            const options = getJsonValue(data, optionsColKey, []);
            const answers = getJsonValue(data, optionsAnswersKey, []);
            if (checkNullArr(stmts)) setStatements(stmts);
            if (checkNullArr(options)) setOptionColumns(options);

            const flatAnswers = checkNullArr(answers) ? Object.assign({}, ...answers) : {};//{Stmt-Name: 'Selected Option', ...rest}
            if (checkNullJson(flatAnswers)) setOptionAnswers(flatAnswers);
            // log('useEffect.data.answers.flatAnswers:', flatAnswers);
        } catch (e) {
            logErr(e)
        }
    }, [data]);

    return (<div className={'w-100 m-0 p-0 bg-infox'}>
        {/*{checkNullStr(question) && <h1>{question}</h1>}*/}

        {/*header controls*/}
        <Flexbox wrap={''} direction={''}>
            {/*add statement*/}
            {!isPreviewMode() &&
                <Button
                    className={`text-white px-2 py-1 text-nowrap me-1 my-1 ms-0 shadow-none ${addStmtClassName}`}
                    text={'Add Statement'}
                    variant={'primary'}
                    color={''}
                    transitionVariant={'xs'}
                    iconClass={`bi bi-plus fs-normal`}
                    disabled={isBoolTrue(showLoader)}
                    style={{...addStmtStyle}}
                    onClick={handleAddStatement}
                />}

            {/*add option column*/}
            {!isPreviewMode() &&
                <Button
                    className={`text-white px-2 py-1 text-nowrap m-1 m-1 ms-2 shadow-none ${addOptionClassName} ${deleteStmtClassName}`}
                    text={'Add Option'}
                    variant={'primary'}
                    color={''}
                    transitionVariant={'xs'}
                    iconClass={`bi bi-plus fs-normal`}
                    disabled={isBoolTrue(showLoader)}
                    style={{...addOptionStyle}}
                    onClick={handleAddOptionColumn}
                />}
        </Flexbox>

        <Section className={`p-0 m-0 w-100 mt-1 ${borderBoxClassName}`}
                 roundedVariant={roundedVariant}
                 borderColor={borderColor}
                 style={borderBoxStyle}>
            <div className={'w-100 m-0 p-0 scroller scroller-x-auto scroll-dark'}>
                <table border="1" className={`rounded-${roundedVariant} overflow-hidden w-100 ${tableClassName}`}
                       style={{borderColor: borderColor, color: color, ...tableStyle}}>
                    <thead className={''}>
                    <tr className={''}>
                        <th className={'p-1'}>Statements</th>
                        {optionColumns.map((opItem, index) => (<th className={'p-1'} key={index + opItem}>
                            <Flexbox className={'p-1'} justifyAt={'center'} alignAt={'center'}>
                                <Label className={`m-0 p-0 text-nowrap fw-semi-bold ${optionItemClassName}`}
                                       textAt={'start'} color={color} style={optionItemStyle}>{opItem}</Label>

                                <Flexbox justifyAt={'end'} alignAt={'center'}>
                                    {/*update option-column*/}
                                    {!isPreviewMode() &&
                                        <Button
                                            className={`text-primary text-white-hover p-2 ms-2 ${editOptionClassName}`}
                                            circular={true}
                                            width={CONTROL_SIZE}
                                            height={CONTROL_SIZE}
                                            highlightVariant={'primary-full'}
                                            variant={'t'}
                                            color={''}
                                            rippleColor={CONTROL_RIPPLE}
                                            transitionVariant={'xs'}
                                            iconClass={`bi bi-pencil`}
                                            tooltip={'Edit Option'}
                                            style={{...editOptionStyle}}
                                            onClick={() => handleEditOptionColumn(index, opItem)}
                                        />}

                                    {/*delete option-column*/}
                                    {!isPreviewMode() &&
                                        <Button
                                            className={`text-danger text-white-hover p-2 ms-1 ${deleteOptionClassName}`}
                                            circular={true}
                                            width={CONTROL_SIZE}
                                            height={CONTROL_SIZE}
                                            highlightVariant={'danger-full'}
                                            color={''}
                                            variant={'t'}
                                            rippleColor={CONTROL_RIPPLE}
                                            transitionVariant={'xs'}
                                            iconClass={`bi bi-trash`}
                                            tooltip={'Delete Option'}
                                            style={{...deleteOptionStyle}}
                                            onClick={() => handleDeleteOptionColumn(index, opItem)}
                                        />}
                                </Flexbox>
                            </Flexbox>
                        </th>))}
                    </tr>
                    </thead>
                    <tbody className={''}>
                    {statements.map((stmtItem, stmtIndex) => (<tr key={stmtItem.id + 'mm' + stmtIndex}>
                        <td className={''}>
                            <Flexbox className={'p-1'} justifyAt={'between'} alignAt={'center'}>
                                <Label className={`m-0 p-0 text-nowrap fw-semi-bold ${stmtItemClassName}`}
                                       textAt={'start'} color={color} style={stmtItemStyle}>{stmtItem[stmtTextKey]}</Label>

                                <Flexbox justifyAt={'end'} alignAt={'center'}>
                                    {/*update stmt*/}
                                    {!isPreviewMode() &&
                                        <Button
                                            className={`text-primary text-white-hover p-2 ms-1 ${editStmtClassName}`}
                                            circular={true}
                                            width={CONTROL_SIZE}
                                            height={CONTROL_SIZE}
                                            highlightVariant={'primary-full'}
                                            variant={'t'}
                                            color={''}
                                            rippleColor={CONTROL_RIPPLE}
                                            transitionVariant={'xs'}
                                            iconClass={`bi bi-pencil`}
                                            tooltip={'Edit Statement'}
                                            style={{...editStmtStyle}}
                                            onClick={() => handleEditStatement(getJsonValue(stmtItem, stmtIdKey), getJsonValue(stmtItem, stmtTextKey), stmtIndex)}
                                        />}

                                    {/*delete stmt*/}
                                    {!isPreviewMode() &&
                                        <Button
                                            className={`text-danger text-white-hover p-2 ms-1 ${deleteStmtClassName}`}
                                            circular={true}
                                            width={CONTROL_SIZE}
                                            height={CONTROL_SIZE}
                                            highlightVariant={'danger-full'}
                                            color={''}
                                            variant={'t'}
                                            rippleColor={CONTROL_RIPPLE}
                                            transitionVariant={'xs'}
                                            iconClass={`bi bi-trash`}
                                            tooltip={'Delete Statement'}
                                            style={{...deleteStmtStyle}}
                                            onClick={() => handleDeleteStatement(getJsonValue(stmtItem, stmtIdKey), stmtIndex)}
                                        />}
                                </Flexbox>
                            </Flexbox>
                        </td>
                        {optionColumns.map((optionCol, optionIndex) => {
                            let defChecked = null;
                            if (isBoolTrue(disableSelection)) {
                                const stmtName = getJsonValue(stmtItem, stmtTextKey);
                                const ansValue = getJsonValue(optionAnswers, stmtName);
                                if (checkNullStr(ansValue)) {
                                    defChecked = optionCol == ansValue;
                                    if (defChecked) {
                                        // log(`stmtName[${stmtIndex}]:`, stmtName, `optionCol[${optionIndex}]:`, optionCol, 'ansValue:', ansValue, 'defChecked:', defChecked);
                                    }
                                }
                            }
                            // log(`optionAnswers[${optionIndex}].text:`, getJsonValue(getArrIndexValue(optionAnswers, optionIndex),))
                            return <td key={optionIndex + 'xx' + stmtItem.id}>
                                <Flexbox className={'p-1'} justifyAt={'center'}>
                                    {isBoolTrue(disableSelection) ?
                                        <span
                                            style={{
                                                width: '12px',
                                                height: '12px',
                                                backgroundColor: defChecked ? '#0080ff' : '#e5f0ff',
                                                border: `1.5px solid ${defChecked ? '#006dd3' : '#c7e0ff'}`,
                                                borderRadius: '100rem'
                                            }}/>
                                        : <input
                                            className={`${(!isBoolTrue(disableSelection) && !isBoolTrue(previewMode)) ? 'cursor-pointer' : ''}
                                        ${isBoolTrue(disableSelection) ? 'disable-item-xsx' : ''}`}
                                            disabled={isBoolTrue(disableSelection)}
                                            type={'radio'}
                                            name={`option_${stmtItem.id}`}
                                            // defaultChecked={optionColumns.includes(getJsonValue(getArrIndexValue(optionAnswers, optionIndex, ''), getJsonValue(stmtItem, stmtIdKey)))}
                                            // checked={isBoolTrue(disableSelection) ? defChecked : null}
                                            // checked={defChecked}
                                            // checked={isBoolTrue(disableSelection) ? defChecked : getArrIndexValue(optionColumns, optionIndex, null) == optionCol}
                                            // defaultChecked={defChecked}
                                            // defaultChecked={getJsonValue(stmtItem, stmtTextKey) == getArrIndexValue(optionColumns, optionIndex, null)}
                                            // checked={getJsonValue(stmtItem, stmtTextKey) == getArrIndexValue(optionColumns, optionIndex, null)}
                                            onChange={((e) => {
                                                // printLog(TAG, 'onChange.e:', e);
                                                let updatedData = selectedOptions;
                                                updatedData[getJsonValue(stmtItem, stmtTextKey)] = getArrIndexValue(optionColumns, optionIndex, null);
                                                setSelectedOptions(updatedData);
                                                if (onPreviewUpdate) onPreviewUpdate(updatedData, optionIndex);
                                                // printLog(TAG, 'onChange.updatedData:', updatedData);


                                                // let updatedDataIndices = selectedOptionsIndices;
                                                // updatedDataIndices[stmtIndex] = getArrIndexValue(optionColumns, optionIndex, null);
                                                // setSelectedOptionsIndices(updatedDataIndices);
                                            })}
                                        />}
                                </Flexbox>
                            </td>
                        })}
                    </tr>))}
                    </tbody>
                </table>
            </div>
        </Section>
    </div>);


    function handleAddStatement() {
        const fun = 'handleAddStatement:';
        try {
            const newStmt = window.prompt('Add new statement');
            if (checkNullStr(newStmt)) {
                const newStatement = {
                    id: statements.length + 1, statement: newStmt,
                };
                const updatedStatements = [...statements, newStatement];
                setStatements(updatedStatements);
                sendUpdateCallback(updatedStatements, optionColumns);
            }
        } catch (e) {
            logErr(fun, e)
        }
    }

    function handleDeleteStatement(id) {
        const fun = 'handleDeleteStatement:';
        try {
            if (window.confirm('Are you sure you want to delete this statement?')) {
                const updatedStatements = statements.filter((stmtItem) => stmtItem.id !== id);
                setStatements(updatedStatements);
                sendUpdateCallback(updatedStatements, optionColumns);
            }
        } catch (e) {
            logErr(fun, e)
        }
    }

    function handleEditStatement(id, oldName, stmtIndex) {
        const fun = 'handleEditStatement:';
        try {
            const newStmt = window.prompt('Update Statement', oldName);
            if (checkNullStr(newStmt)) {
                const updatedStatements = statements.map((stmtItem) => stmtItem.id == id ? {
                    ...stmtItem,
                    statement: newStmt || stmtItem[stmtTextKey]
                } : stmtItem);
                setStatements(updatedStatements);
                sendUpdateCallback(updatedStatements, optionColumns);
            }
        } catch (e) {
            logErr(fun, e)
        }
    }

    function handleAddOptionColumn() {
        const fun = 'handleAddOptionColumn:';
        try {
            const newOption = window.prompt('Add new option');
            if (checkNullStr(newOption)) {
                const updatedOptionColumns = ([...optionColumns, newOption]);
                setOptionColumns(updatedOptionColumns);
                sendUpdateCallback(statements, updatedOptionColumns);
            }
        } catch (e) {
            logErr(fun, e)
        }
    }

    function handleDeleteOptionColumn(index) {
        const fun = 'handleDeleteOptionColumn:';
        try {
            if (window.confirm('Are you sure you want to delete this option?')) {
                const updatedOptionColumns = [...optionColumns];
                updatedOptionColumns.splice(index, 1);
                setOptionColumns(updatedOptionColumns);
                sendUpdateCallback(statements, updatedOptionColumns);
            }
        } catch (e) {
            logErr(fun, e)
        }
    }

    function handleEditOptionColumn(index, oldName) {
        const fun = 'handleEditOptionColumn:';
        try {
            const updatedName = window.prompt('Update option name:', oldName);
            if (checkNullStr(updatedName)) {
                const updatedOptionColumns = [...optionColumns];
                updatedOptionColumns[index] = updatedName;
                setOptionColumns(updatedOptionColumns);
                sendUpdateCallback(statements, updatedOptionColumns);
            }
        } catch (e) {
            logErr(fun, e)
        }
    }

    function sendUpdateCallback(stmts = statements, options = optionColumns) {
        if (onUpdate) onUpdate({[stmtsKey]: stmts, [optionsColKey]: options});
    }

    function isPreviewMode() {
        return isBoolTrue(previewMode);
    }

    function log(...text) {
        printLog(TAG, ...text);
    }

    function logErr(...text) {
        printError(TAG, ...text);
    }
});

export default LikertView;

